import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Avatar
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat
} from '@mui/icons-material';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: 'up' | 'down' | 'flat';
  trendValue?: string;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  icon?: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  trend,
  trendValue,
  color = 'primary',
  icon,
  size = 'medium'
}) => {
  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <TrendingUp color="success" fontSize="small" />;
      case 'down':
        return <TrendingDown color="error" fontSize="small" />;
      case 'flat':
        return <TrendingFlat color="warning" fontSize="small" />;
      default:
        return null;
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up':
        return 'success';
      case 'down':
        return 'error';
      case 'flat':
        return 'warning';
      default:
        return 'default';
    }
  };

  const cardHeight = size === 'small' ? 80 : size === 'large' ? 130 : 100; // Fixed heights to prevent cutoff
  const titleVariant = size === 'small' ? 'caption' : 'body2';
  const valueVariant = size === 'small' ? 'h6' : size === 'large' ? 'h4' : 'h5';

  return (
    <Card 
      sx={{ 
        height: cardHeight,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: `4px solid`,
        borderLeftColor: `${color}.main`,
        '&:hover': {
          boxShadow: 2,
          transform: 'translateY(-1px)',
          transition: 'all 0.2s ease-in-out'
        }
      }}
    >
      <CardContent sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        p: size === 'small' ? 1 : 1.5, // Reduced padding
        '&:last-child': { pb: size === 'small' ? 1 : 1.5 } // Reduced padding
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}> {/* Reduced margin */}
          {icon && (
            <Avatar
              sx={{
                width: size === 'small' ? 20 : 28, // Reduced sizes
                height: size === 'small' ? 20 : 28, // Reduced sizes
                bgcolor: `${color}.light`,
                color: `${color}.main`,
                mr: 0.75 // Reduced margin
              }}
            >
              {icon}
            </Avatar>
          )}
          <Typography 
            variant={titleVariant} 
            color="text.secondary" 
            sx={{ fontWeight: 500 }}
          >
            {title}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 'auto' }}>
          <Typography 
            variant={valueVariant} 
            color={`${color}.main`}
            sx={{ fontWeight: 700 }}
          >
            {value}
          </Typography>
          
          {trend && trendValue && (
            <Chip
              icon={getTrendIcon(trend)}
              label={trendValue}
              size="small"
              color={getTrendColor(trend) as any}
              variant="outlined"
              sx={{ 
                fontSize: '0.7rem',
                height: 20
              }}
            />
          )}
        </Box>
        
        {subtitle && (
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ mt: 'auto' }}
          >
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default MetricCard;
